import React from 'react'
import {
  IconBook,
  IconFile,
  IconFileText,
  IconLanguage,
  IconLogout,
  IconMailPlus,
  IconMoon,
  IconPower,
  IconSun,
  IconUser,
  IconWorld,
} from '@tabler/icons-react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { ActionIcon, Box, Group, Menu, Switch, Text, Tooltip, useMantineColorScheme } from '@mantine/core'
import { useAuth } from '@/contexts/AuthContext'
import { useFlow } from '@/contexts/FlowContext'
import { updateMyPreferences } from '@/routes/users'
import { logout } from '@/services/AuthService'
import { changeLanguage, getCurrentLanguage } from '@/services/languageService'
import AgAvatar from '../atoms/Avatar'

const languages = [
  { code: 'es', label: 'Español', flag: '🇪🇸' },
  { code: 'en', label: 'English', flag: '🇬🇧' },
  { code: 'pt', label: 'Português', flag: '🇵🇹' },
]

export default function AgHeaderMantine() {
  const { t } = useTranslation()
  const { me } = useAuth()
  const { flow } = useFlow()
  const isAuthenticated = !!me
  const current = getCurrentLanguage()
  const navigate = useNavigate()
  const { colorScheme, toggleColorScheme } = useMantineColorScheme()
  const isDark = colorScheme === 'dark'

  const onProfile = () => navigate('/me')
  const onLogout = () => logout()

  const handleThemeToggle = async () => {
    const newTheme = isDark ? 'light' : 'dark'
    toggleColorScheme()
    // Save preference to backend if authenticated
    if (isAuthenticated) {
      try {
        await updateMyPreferences({ theme_preference: newTheme })
      } catch {
        // Silently fail - theme is already applied locally
      }
    }
  }

  // Logo URL from backend
  const logoUrl = `${(import.meta.env.VITE_API_BASE_URL || '').replace(/\/+$/, '')}/static/${isDark ? '2' : '4'}.svg`

  // Theme-aware colors
  const accentColor = isDark ? '#00ff00' : '#00aa00'
  const borderColor = isDark ? 'rgba(0, 255, 0, 0.2)' : 'rgba(0, 100, 0, 0.15)'
  const hoverBg = isDark ? 'rgba(0, 255, 0, 0.1)' : 'rgba(0, 150, 0, 0.08)'

  return (
    <Box
      component="header"
      px="lg"
      py="sm"
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        background: isDark ? 'rgba(0, 0, 0, 0.85)' : 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(12px)',
        borderBottom: `1px solid ${borderColor}`,
      }}
    >
      {/* Logo */}
      <Box
        onClick={() => navigate(isAuthenticated ? '/home' : '/')}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 12 }}
      >
        <img
          src={logoUrl}
          alt="Asbestos.ai"
          style={{
            height: 40,
            filter: 'none',
          }}
        />
      </Box>

      {/* Right side - Logout button + User menu */}
      <Group gap={25}>
      {isAuthenticated && (
        <Tooltip label={t('header.logout')}>
          <button
            type="button"
            onClick={onLogout}
            style={{
              appearance: 'none',
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: 4,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: accentColor,
              transition: 'color 0.2s ease',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.color = '#ff6b6b' }}
            onMouseLeave={(e) => { e.currentTarget.style.color = accentColor }}
          >
            <IconPower size={20} stroke={2} />
          </button>
        </Tooltip>
      )}
      <Menu
        position="bottom-end"
        shadow="xl"
        width={260}
        withArrow
        styles={{
          dropdown: {
            background: isDark ? 'rgba(0, 0, 0, 0.95)' : 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(12px)',
            border: `1px solid ${isDark ? 'rgba(0, 255, 0, 0.3)' : 'rgba(0, 100, 0, 0.15)'}`,
          },
          item: {
            color: isDark ? 'rgba(255, 255, 255, 0.9)' : '#4d4dff',
            '&:hover': {
              background: hoverBg,
            },
            '&[data-disabled]': {
              color: isDark ? 'rgba(255, 255, 255, 0.3)' : 'rgba(0, 0, 0, 0.3)',
            },
          },
          label: {
            color: accentColor,
            fontWeight: 600,
          },
          divider: {
            borderColor: isDark ? 'rgba(0, 255, 0, 0.2)' : 'rgba(0, 100, 0, 0.12)',
          },
          arrow: {
            borderColor: isDark ? 'rgba(0, 255, 0, 0.3)' : 'rgba(0, 100, 0, 0.15)',
            background: isDark ? 'rgba(0, 0, 0, 0.95)' : 'rgba(255, 255, 255, 0.98)',
          },
        }}
      >
        <Menu.Target>
          <button
            type="button"
            aria-haspopup="menu"
            aria-label={isAuthenticated ? `${me?.firstName} ${me?.lastName}` : 'Open user menu'}
            style={{
              appearance: 'none',
              border: `2px solid ${isDark ? 'rgba(0, 255, 0, 0.3)' : 'rgba(0, 150, 0, 0.3)'}`,
              background: isDark ? 'rgba(0, 255, 0, 0.1)' : 'rgba(0, 150, 0, 0.05)',
              padding: 4,
              cursor: 'pointer',
              display: 'inline-flex',
              borderRadius: '50%',
              transition: 'all 0.2s ease',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.borderColor = accentColor
              e.currentTarget.style.background = isDark
                ? 'rgba(0, 255, 0, 0.2)'
                : 'rgba(0, 150, 0, 0.1)'
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.borderColor = isDark
                ? 'rgba(0, 255, 0, 0.3)'
                : 'rgba(0, 150, 0, 0.3)'
              e.currentTarget.style.background = isDark
                ? 'rgba(0, 255, 0, 0.1)'
                : 'rgba(0, 150, 0, 0.05)'
            }}
          >
            {isAuthenticated ? (
              <AgAvatar firstName={me?.firstName || '?'} lastName={me?.lastName || '?'} size={40} />
            ) : (
              <IconWorld stroke={2} color={accentColor} size={32} />
            )}
          </button>
        </Menu.Target>

        <Menu.Dropdown>
          {isAuthenticated && (
            <>
              <Menu.Label>
                <Text size="sm" c={isDark ? 'white' : 'dark'} fw={500}>
                  {`${me!.firstName} ${me!.lastName}`}
                </Text>
              </Menu.Label>
              <Menu.Item
                leftSection={<IconUser size={16} color={accentColor} />}
                onClick={onProfile}
              >
                {t('header.profile')}
              </Menu.Item>
              <Menu.Item
                leftSection={<IconFile size={16} color={accentColor} />}
                component={Link}
                to="/my-documents"
              >
                {t('header.my_documents')}
              </Menu.Item>
              <Menu.Item
                leftSection={<IconMailPlus size={16} color={accentColor} />}
                component={Link}
                to={`/entity/${me?.scopeType}/${me?.scopeId}/invites`}
              >
                {t('header.open_invites')}
              </Menu.Item>
              {flow === 'admin' && (
                <>
                  <Menu.Item
                    leftSection={<IconFileText size={16} color={accentColor} />}
                    component={Link}
                    to="/admin/logs"
                  >
                    {t('header.logs', 'Logs')}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconFileText size={16} color={accentColor} />}
                    component={Link}
                    to="/admin/municipal-assets"
                  >
                    {t('header.municipal_assets', 'Bienes municipales')}
                  </Menu.Item>
                  <Menu.Item
                    leftSection={<IconFileText size={16} color={accentColor} />}
                    component={Link}
                    to="/admin/priority-bulk"
                  >
                    {t('header.priority_bulk', 'Prioridad de retirada')}
                  </Menu.Item>
                </>
              )}
              <Menu.Item
                leftSection={<IconBook size={16} color={accentColor} />}
                component={Link}
                to="/user-manual"
              >
                {t('header.user_manual', 'User Manual')}
              </Menu.Item>
              <Menu.Divider />
            </>
          )}

          {/* Theme Toggle */}
          <Menu.Label>
            <Group gap="xs">
              {isDark ? (
                <IconMoon size={16} color={accentColor} />
              ) : (
                <IconSun size={16} color={accentColor} />
              )}
              {t('header.theme', 'Tema')}
            </Group>
          </Menu.Label>
          <Box px="sm" py="xs">
            <Group justify="space-between">
              <Group gap="xs">
                <IconSun size={16} color={isDark ? 'rgba(255,255,255,0.5)' : accentColor} />
                <Text size="sm" c={isDark ? 'rgba(255,255,255,0.7)' : 'dark'}>
                  {t('header.lightMode', 'Claro')}
                </Text>
              </Group>
              <Switch
                checked={isDark}
                onChange={handleThemeToggle}
                size="md"
                color="green"
                onLabel={<IconMoon size={14} />}
                offLabel={<IconSun size={14} />}
              />
              <Group gap="xs">
                <Text size="sm" c={isDark ? 'rgba(255,255,255,0.7)' : 'dark'}>
                  {t('header.darkMode', 'Oscuro')}
                </Text>
                <IconMoon size={16} color={isDark ? accentColor : 'rgba(0,0,0,0.5)'} />
              </Group>
            </Group>
          </Box>
          <Menu.Divider />

          {/* Language Selection */}
          <Menu.Label>
            <Group gap="xs">
              <IconLanguage size={16} color={accentColor} /> {t('header.language')}
            </Group>
          </Menu.Label>
          {languages.map((lang) => (
            <Menu.Item
              key={lang.code}
              onClick={() => changeLanguage(lang.code)}
              disabled={lang.code === current}
              style={lang.code === current ? { background: hoverBg } : undefined}
            >
              <Group gap="sm">
                <span style={{ fontSize: 18 }}>{lang.flag}</span>
                <span>{lang.label}</span>
                {lang.code === current && (
                  <Text size="xs" c={accentColor} ml="auto">
                    ✓
                  </Text>
                )}
              </Group>
            </Menu.Item>
          ))}
        </Menu.Dropdown>
      </Menu>
      </Group>
    </Box>
  )
}
